import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../interfaces/user';
import { authenticationService } from '../services/authentication.service';
import { Envelope } from '../../interfaces/envelope';
import { Role } from '../../interfaces/types';

interface AuthenticationState {
	loggedIn: boolean;
	tenancyStatus: boolean;
	withoutToken: boolean;
	isLoading: boolean;
	user: User | undefined;
	userRole: Role | undefined;
}

const initialState = {
	loggedIn: false,
	tenancyStatus: false,
	withoutToken: false,
	isLoading: false,
	user: undefined,
} as AuthenticationState;

export const authenticateUser = createAsyncThunk<
	User,
	{ username: string; password: string; withToken: boolean },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('authentication/authenticate', async (data, { rejectWithValue }) => {
	try {
		const token = await authenticationService.authentication(
			data.username,
			data.password,
			data.withToken
		);
		return await authenticationService.userAuthentication(token.id);
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const fetchUser = createAsyncThunk<
	User,
	{ id: number },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('authentication/fetchUser', async (data, { rejectWithValue }) => {
	try {
		return await authenticationService.userAuthentication(data.id);
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const logout = createAsyncThunk<
	void,
	void,
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('authentication/logout', async (_undefined, { rejectWithValue }) => {
	try {
		await authenticationService.logout();
	} catch (error) {
		return rejectWithValue(error);
	}
});

const authenticationSlice = createSlice({
	name: 'authentication',
	initialState,
	reducers: {
		setLoggedInStatus: (state, action: PayloadAction<boolean>) => {
			state.loggedIn = action.payload;
		},
		setTenancyStatus: (state, action: PayloadAction<boolean>) => {
			state.tenancyStatus = action.payload;
		},
		setWithoutToken: (state, action: PayloadAction<boolean>) => {
			state.withoutToken = action.payload;
		},
		setUserRole: (state, action: PayloadAction<Role | undefined>) => {
			state.userRole = action.payload;
		},
		unsetUserRole: (state) => {
			state.userRole = undefined;
		},
		resetAuthentication: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(authenticateUser.fulfilled, (state, action) => {
				state.user = action.payload;
				state.isLoading = false;
			})
			.addCase(authenticateUser.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(authenticateUser.rejected, (state, action) => {
				state.user = undefined;
				state.isLoading = false;
			})
			.addCase(fetchUser.fulfilled, (state, action) => {
				state.user = action.payload;
				state.isLoading = false;
			})
			.addCase(fetchUser.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(fetchUser.rejected, (state, action) => {
				state.user = undefined;
				state.isLoading = false;
			})
			.addCase(logout.fulfilled, (state) => {
				state.user = undefined;
				state.loggedIn = false;
			});
	},
});

export const {
	setLoggedInStatus,
	setTenancyStatus,
	setWithoutToken,
	resetAuthentication,
	setUserRole,
	unsetUserRole,
} = authenticationSlice.actions;

export default authenticationSlice;
